import { defineStore } from "pinia";
import SampleProduct from "@/samples/api/sample_catalogs.json";
import SampleToppingGroup from "@/samples/api/sample_topping_group.json";
import SampleTags from "@/samples/api/sample_tags.json";
import SampleComments from "@/samples/api/sample_comments.json";

import PrinterService from "~/services/thermalPrinter/PrinterService";
import _ from "lodash";
import { getDoc, onSnapshot } from "firebase/firestore";
import axios from "axios";
import APIMenuMapper from "~/utils/apiMenuMapper";
import ProductBuilder from "~/class/ProductBuilder";
import { registerResource, refreshResource, mergeResourceRecords } from "~/services/repos/versionSync";

// Product-related resources tracked by versionSync. Used by `refreshResources`
// so callers can refetch *only* the entities they touched (e.g. editing an item
// refreshes 'items' + 'subitems', not the whole menu).
export type ProductResource =
  | 'catalogs'
  | 'items'
  | 'subitems'
  | 'tags'
  | 'comments'
  | 'toppingGroups'
  | 'toppings'

const ALL_PRODUCT_RESOURCES: ProductResource[] = [
  'catalogs', 'items', 'subitems', 'tags', 'comments', 'toppingGroups', 'toppings',
]

// Entities that support selective per-record (by-id) refetch + merge.
export type RecordEntity = 'catalogs' | 'items' | 'subitems'

export interface RecordChange {
  // Ids whose latest server rows should be fetched and merged in (create/update).
  upsert?: number[]
  // Ids to drop from the cached collection (delete).
  remove?: number[]
}

export type MenuRecordChanges = Partial<Record<RecordEntity, RecordChange>>

// Payload a menu child view emits on a successful mutation:
// - `records` → selective per-record sync of catalogs/items/subitems
// - `groups`  → full refetch of group resources (e.g. tags)
// - omitted   → full refresh (manual Refresh button)
export interface MenuRefresh {
  records?: MenuRecordChanges
  groups?: ProductResource[]
}

// Maps a record entity to its `useDataservice` accessor key (note the camelCase
// `subItems`) and its versionSync resource / mapData type (lowercase).
const RECORD_ENTITY_META: Record<RecordEntity, { dsKey: 'catalogs' | 'items' | 'subItems'; resource: 'catalogs' | 'items' | 'subitems' }> = {
  catalogs: { dsKey: 'catalogs', resource: 'catalogs' },
  items: { dsKey: 'items', resource: 'items' },
  subitems: { dsKey: 'subItems', resource: 'subitems' },
}

export const usePosProductsStore = defineStore("products", {
  state: () => ({
    loaded: false,
    //all new
    catalogs: [],
    items: [],
    subitems: [],
    comments: [],
    toppingGroups: [],
    toppings: [],
    tags: [],

    allData: null,
    uuidPrintersMap: {
      // Full item tags per sub-item id, e.g. '1234' : ['printer1', 'skip'].
      // Feeds both kitchen routing and the receipt/preview "skip" filter.
    },
  }),

  getters: {
    products: (state) => {

      // compute catalogs, items, subitems , itemTags
      return new ProductBuilder().generateProducts(state.catalogs, state.items, state.subitems, state.tags)
    },
    toppingList: (state) => {
       
      return new ProductBuilder().generateToppingGroups(state.toppingGroups, state.toppings)
    },

    getProductTypes: (state) => {
      const types = [...new Set(state.products.map((item) => item.Type))];
      // Fixed bucket order for the cart type tabs: Food → Drink → Dessert → Other.
      // Matches the report ordering (useProductReport.calcPercentProductTypeAndSort).
      // Unknown/custom types keep their insertion order after these (stable sort).
      const FIXED_TYPE_ORDER = ["food", "drink", "dessert", "other"];
      const orderIndex = (name: any) => {
        const idx = FIXED_TYPE_ORDER.indexOf(String(name || "").toLowerCase());
        return idx === -1 ? FIXED_TYPE_ORDER.length : idx;
      };
      const sorted = types.sort((a, b) => orderIndex(a) - orderIndex(b));
      return ["All", ...sorted];
    },

    getProductHolderWithType: (state) => {
      return state.products.reduce((acc, item) => {
        if (!acc[item.Type]) {
          acc[item.Type] = []; // เก็บ object แรกที่เจอในแต่ละ Type
        }
        acc[item.Type].push(item);
        return acc;
      }, {});
    },

    // todo : no one used
    getProductCategoryWithParentId: (state) => {
      const result = {};
      for (let i = 0; i < state.products.length; i++) {
        const Catalog = state.products[i];
        for (let j = 0; j < Catalog.Items.length; j++) {
          const Item = Catalog.Items[j];
          const SubItems = Item.SubItems;

          if (SubItems.length > 1) {
            for (let k = 0; k < SubItems.length; k++) {
              const SubItem = SubItems[k];
              result[SubItem.Id] = {
                catelogId: Catalog.Id,
                catelogName: Catalog.Name,
                item: SubItem,
                parent: Item
              }
            }
          } else {
            result[Item.Id] = {
              catelogId: Catalog.Id,
              catelogName: Catalog.Name,
              item: Item.SubItems[0],
              parent: Item
            }
          }
        }
      }

      return result;
    },
    getProductTypeHolderWithCatalogId: (state) => {
      return state.products.reduce((acc, item) => {
        if (!acc[item.Id]) {
          acc[item.Id] = {}; // เก็บ object แรกที่เจอในแต่ละ Type
        }
        acc[item.Id] = item;
        return acc;
      }, {});
    },
    getAllProductHolder: (state) => {
      return state.products.reduce(
        (acc, item) => {
          acc["All"].push(item);
          return acc;
        },
        { All: [] }
      );
    },
    getAllProductSubItem: (state) => {
      // ดึง subItems ทั้งหมดจาก products
      const allItems = state.products.flatMap((product) => product.Items);

      // แยกเป็น mainItems และ hasSubItems ตามความยาวของ SubItems
      const singleItems = allItems.filter((item) => item.SubItems.length === 1);
      const multiItems = allItems.filter((item) => item.SubItems.length > 1);

      const result = [];
      for (let i = 0; i < multiItems.length; i++) {
        for (let j = 0; j < multiItems[i].SubItems.length; j++) {
          const data = {
            ...multiItems[i].SubItems[j],
            Name: `${multiItems[i].Name} (${multiItems[i].SubItems[j].Name})`,
            CatalogId: multiItems[i].CatalogId,
          };

          result.push(data);
        }
      }
      // รวมผลลัพธ์กลับ
      return [...singleItems, ...result];
    },

    getAllProductSearch: (state) => {

      let _products = state.products
      const result = [];
      for (let i = 0; i < _products.length; i++) {
        const Catalog = _products[i];
        for (let j = 0; j < Catalog.Items.length; j++) {
          const Item = Catalog.Items[j];
          const SubItems = Item.SubItems;

          if (SubItems.length > 1) {
            for (let k = 0; k < SubItems.length; k++) {
              const SubItem = SubItems[k];
              result.push({
                label: `${Item.Name} (${SubItem.Name})`,
                SubItem: SubItem,
                Parent: Item
              })
            }
          } else {
            result.push({
              label: Item.Name,
              SubItem: Item.SubItems[0],
              Parent: Item
            })
          }
        }
      }
      return result;
    },


    // Full item tags (printer tags + others like "skip") for a sub-item id.
    getPrintersByUuid(state: string) {
      return (uuid: string) => {
        return state.uuidPrintersMap[uuid] || [];
      };
    },

    /**
     * Divana billing classifier (legacy parity): true when a sub-item's catalog
     * `Type` contains "Product" — i.e. a VAT-inclusive, service-exempt good.
     * Fed as `skipServiceResolver` into calculateItems so DivanaBillingStrategy
     * can exclude these lines from the VAT / service base. Keyed by SubItem.Id
     * (same as computeUuidPrintersMap).
     *
     * Only reached when the active strategy declares `usesSkipService` (see
     * utils/billingResolvers) — on the default strategy this getter is never
     * touched, so the map below is never built.
     *
     * The map stores the resolved BOOLEAN, not the raw Type: the returned closure
     * runs once per line on every billing pass, and lowercasing the Type string
     * there allocated a throwaway string each time. Classify once per sub-item at
     * map-build time instead.
     */
    isBillingSkipServiceSubItem: (state) => {
      const skipBySubItemId: Record<string, boolean> = {};
      for (let i = 0; i < state.products.length; i++) {
        const Catalog = state.products[i];
        const type = Catalog.Type;
        const isProduct = typeof type === "string" && type.toLowerCase().includes("product");
        const Items = Catalog.Items || [];
        for (let j = 0; j < Items.length; j++) {
          const SubItems = Items[j].SubItems || [];
          for (let k = 0; k < SubItems.length; k++) {
            skipBySubItemId[SubItems[k].Id] = isProduct;
          }
        }
      }
      return (subItemId: any) => {
        // Ids arrive as both number and string depending on the call path.
        return skipBySubItemId[subItemId] ?? skipBySubItemId[String(subItemId)] ?? false;
      };
    },
  },

  actions: {
    init() {
      this.registerResources()
    },

    /**
     * Item-level default discount mapper — the single source of truth for both
     * ordering entry points (cart save via pos.cart._buildOrder, and mobile /
     * job-gateway receive via AdapterMobileV1), mirroring combo `expandItems`.
     *
     * A cart/order line carries a SubItem (`subItemId`); the default discount is
     * configured one level up, on the parent Item (`DiscountMode`/`DiscountValue`,
     * mode 0 = percent, 1 = fixed baht). This walks subItemId → parent Item and,
     * when the Item has a non-zero default discount, stamps `discountrequest`
     * (lowercase — the field OrderBuilder forwards and JobchainV1 normalises to
     * `discountRequest` on replay) onto the line.
     *
     * Idempotent & non-destructive: returns shallow-cloned lines; never touches a
     * line that already carries a `discountrequest` (manual override / already
     * applied in the cart), was explicitly cleared (`noDefaultDiscount`), or is a
     * combo child line. Lines with no matching item / zero discount pass through.
     */
    applyDefaultDiscount(items: any[]): any[] {
      if (!Array.isArray(items) || items.length === 0) return items

      const itemById = new Map<number, any>(
        (this.items as any[]).map((it) => [Number(it.Id), it])
      )
      const subitemById = new Map<number, any>(
        (this.subitems as any[]).map((s) => [Number(s.Id), s])
      )

      return items.map((line: any) => {
        // Respect existing/cleared discounts and never discount free combo children.
        if (line?.discountrequest || line?.noDefaultDiscount || line?.isComboChild) {
          return line
        }

        const subId = Number(line?.subItemId ?? line?.subitemid)
        if (!subId) return line

        const sub = subitemById.get(subId)
        const parent = sub ? itemById.get(Number(sub.ItemId)) : undefined
        if (!parent) return line

        const value = Number(parent.DiscountValue) || 0
        if (!value) return line

        const mode = Number(parent.DiscountMode) || 0
        return { ...line, discountrequest: { mode, value } }
      })
    },

    /**
     * Register every product-related resource with versionSync. Bootstrap's
     * `syncAllVersions()` then drives the actual fetch/cache flow. After it
     * resolves, bootstrap sets `loaded = true` for this store.
     */
    registerResources() {
      registerResource({
        resource: 'catalogs',
        refetch: () => useDataservice().catalogs.fetch(),
        applyToStore: (raw: any) => { this.catalogs = APIMenuMapper.mapData(raw, 'catalogs') },
      })
      registerResource({
        resource: 'items',
        refetch: () => useDataservice().items.fetch(),
        applyToStore: (raw: any) => { this.items = APIMenuMapper.mapData(raw, 'items') },
      })
      registerResource({
        resource: 'subitems',
        refetch: () => useDataservice().subItems.fetch(),
        applyToStore: (raw: any) => { this.subitems = APIMenuMapper.mapData(raw, 'subitems') },
      })
      registerResource({
        resource: 'tags',
        refetch: () => useDataservice().itemTags.fetch(),
        applyToStore: (data: any) => { this.tags = data },
      })
      registerResource({
        resource: 'comments',
        refetch: () => useDataservice().comments.fetch(),
        applyToStore: (data: any) => { this.comments = data },
      })
      registerResource({
        resource: 'toppingGroups',
        refetch: () => useDataservice().toppingGroups.fetch(),
        applyToStore: (data: any) => { this.toppingGroups = data },
      })
      registerResource({
        resource: 'toppings',
        refetch: () => useDataservice().toppingItems.fetch(),
        applyToStore: (data: any) => { this.toppings = data },
      })
    },

    /**
     * Refetch the given product resources from the cloud, update the IDB cache,
     * bump their Firestore version hash, and apply the fresh data into this
     * store. Pass the specific resources that were mutated so only those are
     * re-fetched (saves bandwidth vs. reloading the whole menu); omit to refresh
     * everything (manual "Refresh" button).
     */
    async refreshResources(resources?: ProductResource[]) {
      const list = resources?.length ? resources : ALL_PRODUCT_RESOURCES
      await Promise.all(list.map((r) => refreshResource(r)))
    },

    /**
     * Selective per-record sync for catalogs/items/subitems after a menu
     * mutation. For each entity: fetch only the upserted ids by id, then merge
     * those rows (and drop removed ids) into the cached collection + store +
     * Firestore version. The reactive `products` getter re-derives automatically
     * so the POS selling screen reflects the change with no reload; we also
     * recompute the printer-tag map for kitchen routing.
     */
    async syncRecords(changes: MenuRecordChanges) {
      const entries = (Object.keys(changes) as RecordEntity[])
        .map((entity) => [entity, changes[entity]] as const)
        .filter((pair): pair is [RecordEntity, RecordChange] => !!pair[1])

      await Promise.all(entries.map(async ([entity, change]) => {
        const { dsKey, resource } = RECORD_ENTITY_META[entity]
        let upsertRaw: Array<{ data: unknown[] }> = []
        if (change.upsert?.length) {
          const ds = useDataservice() as any
          const raw: any = await ds[dsKey].fetchByIds(change.upsert)
          upsertRaw = Array.isArray(raw) ? raw : (raw?.data ?? [])
        }
        await mergeResourceRecords(resource, { upsertRaw, removeIds: change.remove })
      }))

      // Keep kitchen printer routing in sync with menu/tag edits (otherwise only
      // computed at bootstrap finishLoad).
      this.computeUuidPrintersMap()
    },

    /** Mark this store as loaded once bootstrap has run syncAllVersions. */
    finishLoad() {
      this.computeUuidPrintersMap()
      this.loaded = true
    },

    // todo: remove no one use it
    computeUuidPrintersMap() {

      const result = {};
      for (let i = 0; i < this.products.length; i++) {
        const Catalog = this.products[i];
        for (let j = 0; j < Catalog.Items.length; j++) {
          const Item = Catalog.Items[j];
          // Carry the FULL, untouched item tag list — exactly like the legacy
          // `subItemIdHolder` (thermalprinter.v2.js). Two things depend on it:
          //   1. Kitchen grouping: sub-station suffixes ("printer1-1",
          //      "printer1-2") must stay intact so the print service splits one
          //      slip per distinct tag. (The old substring(0,8) collapsed them
          //      to "printer1" → one big slip.)
          //   2. Receipt/preview "skip": buffet lines tagged "skip" are filtered
          //      by the print service. (The old .filter(includes "printer")
          //      dropped "skip" → it never reached the printer.)
          // Non-printer tags are harmless to the kitchen (it only groups tags
          // matching its TagLooking, e.g. "printer1").
          const Tags = Item.Tags || [];
          const SubItems = Item.SubItems;
          for (let k = 0; k < SubItems.length; k++) {
            const SubItem = SubItems[k];
            result[SubItem.Id] = Tags;
          }
        }
      }

      this.uuidPrintersMap = result;
      ;

      return result;
    },

    // todo: remove no one use it
    getProductGroupsByItemHolder() {
      let groupHolder = {};

      _.forEach(this.products, (item: any) => {
        if (groupHolder[item.Id] === undefined) {
          groupHolder[item.Id] = {};
        }

        let newGroup = { ...item };
        delete newGroup.Items;
        groupHolder[item.Id] = newGroup;
      });

      //Make Item Holder [itemId]:groupData
      let products = {};
      const getAllProductSubItem = this.getAllProductSubItem;
      _.forEach(getAllProductSubItem, (item: any) => {
        products[item.Id] = groupHolder[item.CatalogId];
      });
      return products;
    },

  },
});
